import React, { useEffect, useState } from 'react';
import { connect } from 'redux-bundler-react';

import { ModalContent, ModalFooter } from '@components/modal';
import SelectInput from '@components/select/Select';
import { toast } from 'react-toastify';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';
import { Alert } from '@trussworks/react-uswds';
import DistrictModal from '@components/map/modals/districtModal';

import { latRegex, lngRegex, cityRegex, zipCodeRegex, decimalNumberRegex } from '@src/utils/regex';
import ProjectLocationTab from '@src/app-pages/Home/components/modals/modal-tabs/project-location-tab/ProjectLocationTab';
import { formatCoordFlt } from '@src/utils/helpers';
import { useRequest, useModal, usePlss } from '@stores';
import { useAuth } from '@hooks';
import { ErrorMessages, RequestType } from '@src/utils/enums';
import { useGetDistrictLocationFromGeometry } from '@src/api/services/aor/hooks';

const defaultValues = {
  country: 'US',
  showAddressInput: false,
  showGDBInput: false,
  showMapInput: false,
};

const isAnonymousModal = connect(
  'doUpdateIsAnonymous',
  'doUpdateRequestObject',
  'doResetReduxFormData',
  'doUpdateRequestData',
  'doAddViolationComplaint',
  'doAddAnonymousViolationComplaint',
  'doUpdateRequestLocation',
  ({
    doUpdateIsAnonymous,
    doUpdateRequestObject,
    doResetReduxFormData,
    doUpdateRequestData,
    doAddViolationComplaint,
    doAddAnonymousViolationComplaint,
    doUpdateRequestLocation,
  }) => {
    const [projectOutsideUS, setProjectOutsideUS] = useState(false);
    const [district, setDistrict] = useState();

    const { openSecondaryModal } = useModal();
    const { login, isAuthenticated } = useAuth();
    const { plssData } = usePlss();
    const { projectGeometry, resetRequest, setIsNewRequest, setSelectedRequestId } = useRequest();
    const districtLocations = useGetDistrictLocationFromGeometry(projectGeometry);

    const schema = yup
      .object()
      .shape(
        {
          showAddressInput: yup.boolean().nullable(),
          showMapInput: yup.boolean().nullable(),
          showGDBInput: yup.boolean().nullable(),
          addressInputType: yup
            .string()
            .nullable()
            .when('showAddressInput', {
              is: (showAddressInput) => showAddressInput === true,
              then: () => yup.string().required('Please select an option'),
            }),
          address: yup
            .string()
            .nullable()
            .when(['addressInputType', 'showAddressInput'], {
              is: (addressType, showAddressInput) => addressType?.includes('address') && showAddressInput === true,
              then: () => yup.string().required('Field is required'),
            }),
          addressTwo: yup.string().nullable(),
          city: yup
            .string()
            .nullable()
            .when(['addressInputType', 'showAddressInput'], {
              is: (addressType, showAddressInput) => addressType?.includes('address') && showAddressInput === true,
              then: () =>
                yup
                  .string()
                  .matches(cityRegex, { message: 'Field is invalid', excludeEmptyString: true })
                  .required('Field is required'),
            }),
          state: yup
            .string()
            .nullable()
            .when(['addressInputType', 'showAddressInput'], {
              is: (addressType, showAddressInput) => addressType?.includes('address') && showAddressInput === true,
              then: () => yup.string().required('Field is required'),
            }),
          zipcode: yup
            .string()
            .nullable()
            .when(['addressInputType', 'showAddressInput'], {
              is: (addressType, showAddressInput) => addressType?.includes('address') && showAddressInput === true,
              then: () =>
                yup
                  .string()
                  .matches(zipCodeRegex, { message: 'Field is invalid', excludeEmptyString: true })
                  .required('Field is required'),
            }),
          county: yup.string().nullable().matches(cityRegex, { message: 'Field is invalid', excludeEmptyString: true }),
          country: yup
            .string()
            .nullable()
            .when(['addressInputType', 'showAddressInput'], {
              is: (addressType, showAddressInput) => addressType?.includes('address') && showAddressInput === true,
              then: () => yup.string().required('Field is required'),
            }),
          latitude: yup
            .string()
            .nullable()
            .when(['addressInputType', 'showAddressInput'], {
              is: (addressType, showAddressInput) => addressType?.includes('coordinates') && showAddressInput === true,
              then: () =>
                yup
                  .string()
                  .required('Field is required')
                  .test({
                    name: 'latInRange',
                    exclusive: true,
                    message: 'Latitude must be between 0 and 90',
                    test: (val) => (!projectOutsideUS ? Number(val) <= 90 && Number(val) >= 0 : true),
                  })
                  .test(
                    'latFormat',
                    'Latitude format is incorrect. Must be +-XX.XXXXXX and include at least 6 decimal places.',
                    (val) => latRegex.test(val)
                  ),
            }),
          longitude: yup
            .string()
            .nullable()
            .when(['addressInputType', 'showAddressInput'], {
              is: (addressType, showAddressInput) => addressType?.includes('coordinates') && showAddressInput === true,
              then: () =>
                yup
                  .string()
                  .required('Longitude is required')
                  .test({
                    name: 'lngInRange',
                    exclusive: true,
                    message: 'Longitude must be between -180 and 0',
                    test: (val) => (!projectOutsideUS ? Number(val) <= 0 && Number(val) >= -180 : true),
                  })
                  .test(
                    'lngFormat',
                    'Longitude format is incorrect. Must be +-XXX.XXXXXX and include at least 6 decimal places.',
                    (val) => lngRegex.test(val)
                  ),
            }),
          projectArea: yup
            .string()
            .nullable()
            .matches(decimalNumberRegex, { message: 'Field is invalid', excludeEmptyString: true })
            .test(
              'is-required',
              'Area of Violation required when Unit of Measurement has been entered',
              function (value) {
                const { unitOfMeasurement } = this.parent;
                return !unitOfMeasurement || (value !== '' && value !== null);
              }
            ),
          unitOfMeasurement: yup
            .string()
            .nullable()
            .test(
              'is-required',
              'Unit of Measurement required when Area of Violation has been entered',
              function (value) {
                const { projectArea } = this.parent;
                return !projectArea || (value !== '' && value !== null);
              }
            ),
          parcelNumber: yup.string().nullable(),
          nearestWaterbody: yup.string().nullable(),
          fieldOfficeCode: yup.string().nullable(),
          siteDirections: yup.string().nullable(),
          plssSection: yup.string().nullable(),
          plssTownship: yup.string().nullable(),
          plssRange: yup.string().nullable(),
          plssRangeDir: yup.string().nullable(),
          plssTownshipDir: yup.string().nullable(),
          projectLocationGDBFile: yup.mixed().default([]),
        },
        ['projectArea', 'projectArea']
      )
      .test('at-least-one-input', 'Please select a value for either Address, Map, or GDB input', function (value) {
        const { showAddressInput, showMapInput, showGDBInput } = value;
        if (!showAddressInput && !showMapInput && !showGDBInput) {
          return this.createError({ path: 'inputType', message: 'Please select an option' });
        }
        return true;
      })
      .test('geometry-required', 'Map: Please add a point, line or polygon to the map', function (value) {
        const { showMapInput, latitude, longitude } = value;
        if (showMapInput && (!latitude || !longitude)) {
          return this.createError({
            path: 'mapScroll',
            message: 'Map: Please add a point, line or polygon to the map',
          });
        }
        return true;
      })
      .test('file-selection-required', ErrorMessages.GDBFileMissing, function (value) {
        const { showGDBInput, latitude, longitude } = value;
        if (showGDBInput && (!latitude || !longitude)) {
          return this.createError({
            path: 'projectLocationGDBFile',
            message: ErrorMessages.GDBFileMissing,
          });
        }
        return true;
      });

    const methods = useForm({ resolver: yupResolver(schema), mode: 'onBlur', defaultValues: defaultValues });
    const {
      formState: { errors, isValid },
      setValue,
      setFocus,
      watch,
      getValues,
      trigger,
    } = methods;
    const showGDBInput = watch('showGDBInput');
    const showMapInput = watch('showMapInput');
    const addressInputType = watch('addressInputType');
    const isAnonymous = watch('isAnonymous');

    const handleClick = () => {
      if (isAnonymous === 'Yes' || isAuthenticated) {
        if (isValid && districtLocations?.[0]?.district) {
          resetRequest();
          setIsNewRequest(true);
          const data = getValues();

          setSelectedRequestId('6');
          doUpdateRequestObject({ requestType: 'Violation Complaint' });
          doUpdateRequestData({
            projectName: 'Report of Potential Enforcement',
            projectDescription: 'Report of potential unauthorized or non-compliance',
            natureOfActivity: 'Report of potential unauthorized or non-compliance',
            projectPurpose: 'Report of potential unauthorized or non-compliance',
            siteDirections: data?.siteDirections,
            parcelNumber: data?.parcelNumber,
            nearestWaterbody: data?.nearestWaterbody,
          });
          doUpdateIsAnonymous(Boolean(isAnonymous === 'Yes'));

          const locationObj = {
            latitude: formatCoordFlt(data?.latitude),
            longitude: formatCoordFlt(data?.longitude),
            address: data?.address,
            addressTwo: data?.addressTwo,
            city: data?.city,
            state: data?.state,
            zipcode: data?.zipcode,
            county: data?.county,
            country: data?.country,
            district: district,
            fieldOfficeCode: data?.fieldOfficeCode,
            plssSection: plssData?.FRSTDIVNO,
            plssTownship: plssData?.TWNSHPNO,
            plssRange: plssData?.RANGENO,
            plssRangeDir: plssData?.RANGEDIR,
            plssTownshipDir: plssData?.TWNSHPDIR,
            projectArea: Number(data?.projectArea),
            unitOfMeasurement: data?.unitOfMeasurement,
            geometry: projectGeometry,
          };

          doUpdateRequestLocation(locationObj);
          isAnonymous === 'Yes' ? doAddAnonymousViolationComplaint() : doAddViolationComplaint();
        } else if (isValid && !districtLocations?.[0]?.district) {
          alert(
            'Location is not in a valid USACE district or district has not been determined yet. Please verify the project location and try again.'
          );
        } else {
          trigger();
          toast.error('Please fill out all required fields!');
        }
      } else if (!isAnonymous) {
        trigger('isAnonymous');
        toast.error('Please select a value!');
      } else {
        sessionStorage.setItem('VCSession', true);
        login();
      }
    };

    useEffect(() => {
      if (errors?.[Object.keys(errors)[0]]?.['ref']?.focus) {
        errors?.[Object.keys(errors)[0]]?.['ref']?.focus();
      }
      setFocus(errors?.[Object.keys(errors)[0]]?.['ref']?.['id']);
    }, [errors, setFocus]);

    useEffect(() => {
      doResetReduxFormData();
    }, [doResetReduxFormData]);

    useEffect(() => {
      const VCSession = sessionStorage.getItem('VCSession');
      VCSession && setValue('isAnonymous', 'No');
      sessionStorage.removeItem('VCSession');
    }, [setValue]);

    useEffect(() => {
      if (districtLocations?.length > 1 && (!district || !districtLocations.some((d) => d.district === district))) {
        openSecondaryModal(DistrictModal, { setDistrict });
      } else if (districtLocations?.length === 1 && districtLocations?.[0].district !== district) {
        setDistrict(districtLocations?.[0].district);
      }
    }, [districtLocations, district, openSecondaryModal]);

    useEffect(() => {
      doUpdateRequestLocation({ district: district });
    }, [doUpdateRequestLocation, district]);

    const isSaveDisabled = () => {
      if (isAnonymous === 'No') {
        return false;
      }
      return !(addressInputType === 'coordinates' || addressInputType === 'address') && !showMapInput && !showGDBInput;
    };

    return (
      <ModalContent
        className={(isAnonymous === 'Yes' || isAnonymous === 'No') && 'modal-resize'}
        title='Report Potential Violation'
      >
        <FormProvider {...methods}>
          <section className='modal-body'>
            <SelectInput name='isAnonymous' label='Do you wish to remain anonymous' required>
              <option key='2' value='Yes'>
                Yes
              </option>
              <option key='3' value='No'>
                No
              </option>
            </SelectInput>
            {isAnonymous === 'Yes' && (
              <Alert slim noIcon type='warning' heading='NOTICE' headingLevel='h4' className='margin-bottom-3'>
                You will not be receiving update notifications on your submission.
                <br />
                WARNING: If you choose to log in while reporting an anonymous alleged violation, any entered data will
                not be saved to the RRS.
              </Alert>
            )}
            {isAnonymous === 'No' && !isAuthenticated ? (
              <Alert slim noIcon type='warning' heading='NOTICE' headingLevel='h4'>
                You will be prompted to sign-up/sign-in. If signed in, you can track your submissions in the dashboard.
              </Alert>
            ) : isAnonymous === 'No' && isAuthenticated ? (
              <Alert slim noIcon type='warning' heading='NOTICE' headingLevel='h4'>
                Please note, if you reported an alleged violation, USACE does not discuss the details of ongoing
                enforcement investigations with third parties and status updates will not be provided.
              </Alert>
            ) : null}
            {(isAnonymous === 'Yes' || (isAuthenticated && isAnonymous === 'No')) && (
              <ProjectLocationTab
                activeTab={0}
                areaRequired={false}
                locationType='Site'
                setProjectOutsideUS={setProjectOutsideUS}
                requestType={RequestType.ViolationComplaint}
              />
            )}
          </section>
        </FormProvider>
        <ModalFooter
          showCancelButton
          saveText={
            isAnonymous === 'Yes' || isAnonymous === undefined || isAuthenticated ? 'Continue' : 'Sign-Up/Sign-In'
          }
          onSave={() => handleClick()}
          saveIsDisabled={isSaveDisabled()}
          customClosingLogic
        />
      </ModalContent>
    );
  }
);

export default isAnonymousModal;
